import React, { createContext, useEffect } from "react";
import { useSnapshot } from "@web-monorepo/hooks";
import { useSessionQuery } from "#/src/hooks/useSession";
import { useFeatureSwitchAndStartExperiment } from "#/src/hooks/useFeatureSwitches";
import { useMarketplaceTutorRealtime } from "#/src/hooks/useMarketplaceTutorRealtime";
import { tutorRealtimeStore } from "./tutorRealtimeStore";

const TutorRealtimeContext = createContext<typeof tutorRealtimeStore | undefined>(undefined);

export function TutorRealtimeProvider({ children }: { children: React.ReactNode }) {
  const { sessionData } = useSessionQuery();
  const enableLastMinuteSubs = useFeatureSwitchAndStartExperiment("web_marketplace_last_minute_pubnub_sept2025");
  const tutorId = sessionData?.user_details.uid;
  const isInExperiment = enableLastMinuteSubs.isInExperiment;
  const snap = useSnapshot(tutorRealtimeStore);

  useEffect(() => {
    if (!tutorId || isInExperiment === false) {
      return;
    }
    // Only tear down + re-init when the *identity* of the connection changes
    // (a different tutor). `setParams` merges, so light-touch updates don't
    // abort an in-flight connection.
    //
    // NOTE on deps: depending on `enableLastMinuteSubs` (the whole object) is
    // unsafe because `useFeatureSwitchAndStartExperiment` returns a new
    // reference every render. Combined with the `useSnapshot(tutorRealtimeStore)`
    // below — which re-renders this component every time the store's status
    // changes — that would re-fire this effect on every store update.
    const params = tutorRealtimeStore.params;
    const connectionIdentityChanged = params == null || params.tutorId !== tutorId;

    if (connectionIdentityChanged) {
      tutorRealtimeStore.disconnect();
    }
    tutorRealtimeStore.setParams({ tutorId });
  }, [tutorId, isInExperiment]);

  // Subscribe to NATS messages only while the tutor is actively connected for
  // SNTS. The hook tears down cleanly when `enabled` flips to false (e.g. on
  // disconnect()).
  useMarketplaceTutorRealtime({
    tutorId,
    enabled: snap.status._tag === "connected",
  });

  return <TutorRealtimeContext.Provider value={tutorRealtimeStore}>{children}</TutorRealtimeContext.Provider>;
}
